import "./Map.css"
import "mapbox-gl/dist/mapbox-gl.css"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import mapboxgl from "mapbox-gl"
import { circle } from "@turf/turf"
import debounce from "lodash.debounce"
import classNames from "classnames"

import { defaultPinColour, mapPinColours } from "./common"

import { InlineLoading } from "web/components"

import { MAPBOX_CONFIG } from "web/constants"

import useFullHeight from "web/hooks/behaviour/useFullHeight"
import useResizeObserver from "web/hooks/behaviour/useResizeObserver"
import { getCenterLngLat } from "web/services/geography"
import { isNonEmptyArray } from "web/services/helpers"
import { v4 as uuid } from "uuid"

import MapButton from "./MapButton"

import pinImage from "web/images/map-pin.png"
import stickyPinImage from "web/images/map-pin-and-circle.png"

import {
  MAP_MAX_ZOOM_LEVEL,
  MAP_MIN_ZOOM_LEVEL,
  MAP_STYLES,
  MAX_CATEGORIES,
  MINIMUM_BOUNDING_BOX_DIAGONAL,
  UNITS_FROM_CODE,
} from "web/components/Map/MapConstants"

mapboxgl.accessToken = MAPBOX_CONFIG.accessToken

function getMapData(locations, lngLatFn, showOrdinals) {
  locations.forEach((location) => {
    location.icon = location.sticky ? "sticky-pin" : "pin"
    if (!showOrdinals) {
      delete location.ordinal
    }
  })

  return {
    type: "FeatureCollection",
    features: locations.map((location) => ({
      type: "Feature",
      properties: location,
      geometry: {
        type: "Point",
        coordinates: lngLatFn(location),
      },
    })),
  }
}

function fitMapToLocations(map, locations, lngLatFn) {
  const bounds = getBoundsForLocations(locations, lngLatFn)
  const diagonal = bounds.getNorthEast().distanceTo(bounds.getSouthWest())

  if (diagonal <= MINIMUM_BOUNDING_BOX_DIAGONAL) return

  map.fitBounds(bounds, { padding: 100 })
}

function getBoundsForLocations(locations, lngLatFn) {
  const bounds = new mapboxgl.LngLatBounds()
  locations.forEach((location) => {
    bounds.extend(lngLatFn(location))
  })
  return bounds
}

function getIconColourMap(categoryField, categories) {
  if (!categoryField || !isNonEmptyArray(categories)) return defaultPinColour

  const mapBoxSafeValue = (value) =>
    typeof value === "boolean" ? value.toString() : value

  const categoryColours = categories
    .slice(0, MAX_CATEGORIES)
    .map((category, i) => [mapBoxSafeValue(category.value), mapPinColours[i]])
    .flat()

  return [
    "match",
    ["get", categoryField.key, ["get", "values"]],
    ...categoryColours,
    defaultPinColour,
  ]
}

export default function Map({
  mapId,
  locations,
  centralLocation,
  lngLatFn,
  isStatic = false,
  canRecenterMap,
  buttons = [],
  distanceCondition = null,
  children,
  categoryField,
  categories,
  onLocationClick,
  savedMapCenter,
  zoom,
  style,
  handleZoomChange,
  handleMapCenterChange,
  handleStyleChange,
  canEdit = true,
  buttonsLocation = "top",
  showButtonsOnHover = false,
  isLoading,
  showOrdinals = false,
}) {
  const currentMapId = useRef(mapId ? mapId : uuid())
  const mapContainerRef = useRef(null)
  useFullHeight({ enabled: !isStatic, elementId: currentMapId.current })

  const [map, setMap] = useState(null)

  const zoomChangedDebounce = useMemo(
    () =>
      debounce((zoomLevel) => {
        handleZoomChange(zoomLevel)
      }, 500),
    [handleZoomChange]
  )

  const hasCentralLocation = !!centralLocation
  const hasLocations = isNonEmptyArray(locations)

  const mapBoundsNotSaved = !savedMapCenter || !zoom

  const mapCenter = savedMapCenter || getMapCenter(locations)

  //ref needed for Mapbox callback
  const locationsRef = useRef()
  locationsRef.current = locations
  const categoryFieldRef = useRef()
  categoryFieldRef.current = categoryField
  const categoriesRef = useRef()
  categoriesRef.current = categories
  const showOrdinalsRef = useRef()
  showOrdinalsRef.current = showOrdinals

  function getMapCenter(locations) {
    if (hasCentralLocation) {
      return lngLatFn(centralLocation)
    } else {
      return getCenterLngLat(locations.map(lngLatFn))
    }
  }

  function buildDistanceCircle(map, distanceCondition) {
    const centers = distanceCondition.center_points
    if (centers.length === 0) return
    const radius = parseFloat(distanceCondition.value.value)

    const options = {
      steps: 64,
      units: UNITS_FROM_CODE[distanceCondition.value.unit],
    }

    const _circles = {
      type: "FeatureCollection",
      features: centers.map((center) => circle(center, radius, options)),
    }

    map.addSource("distance", {
      type: "geojson",
      data: _circles,
    })
    map.addLayer({
      id: "distance",
      type: "fill",
      source: "distance",
      layout: {},
      paint: {
        "fill-color":
          distanceCondition.operator === "greater_than" ? "#ff0000" : "#0080ff",
        "fill-opacity": 0.2,
      },
    })
  }

  function updateDistanceCircle(map, distanceCondition) {
    const centers = distanceCondition.center_points
    if (centers.length === 0) return
    const radius = parseFloat(distanceCondition.value.value)

    const options = {
      steps: 64,
      units: UNITS_FROM_CODE[distanceCondition.value.unit],
    }

    const _circles = {
      type: "FeatureCollection",
      features: centers.map((center) => circle(center, radius, options)),
    }

    map.getSource("distance").setData(_circles)
    map.setPaintProperty(
      "distance",
      "fill-color",
      distanceCondition.operator === "greater_than" ? "#ff0000" : "#0080ff"
    )
  }

  function removeDistanceCircle(map) {
    map.removeLayer("distance")
    map.removeSource("distance")
  }

  async function saveMapBounds(mapCenter) {
    handleMapCenterChange && handleMapCenterChange(mapCenter)
  }

  function recenterMap(map) {
    map.flyTo({ center: getMapCenter(locations) })
    if (!hasCentralLocation && locations.length > 1) {
      if (isStatic) return
      fitMapToLocations(map, locations, lngLatFn)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setMapTop = useCallback(
    debounce(
      () => {
        const mapContainer = document.getElementById("map-container")
        if (!mapContainer) return
        const mapEl = document.getElementById(currentMapId.current)
        mapEl.style.top = `${mapContainer.offsetTop}px`

        if (map) map.resize()
      },
      100,
      { leading: true }
    ),
    [map]
  )

  useResizeObserver(isStatic ? null : mapContainerRef, setMapTop)

  useEffect(() => {
    if (!isStatic) {
      window.addEventListener("resize", setMapTop)

      return () => {
        window.removeEventListener("resize", setMapTop)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isStatic) {
      setMapTop()
    }
  })

  useEffect(() => {
    if (map && hasCentralLocation) {
      recenterMap(map)
    }
  }, [centralLocation])

  useEffect(() => {
    function handleLocationClick(e) {
      if (onLocationClick) {
        const locationId = e.features[0].properties.id
        const location = locationsRef.current.find(
          (location) => location.id === locationId
        )
        onLocationClick(location)
      }
    }

    function addMapData(map) {
      const circleColor = defaultPinColour
      const textColor = "white"

      map.loadImage(pinImage, (error, image) => {
        if (error) throw error
        map.addImage("pin", image, { sdf: true })

        map.loadImage(stickyPinImage, (error, image) => {
          if (error) throw error
          map.addImage("sticky-pin", image, { sdf: true })

          map.addSource("locations", {
            type: "geojson",
            data: getMapData(
              locationsRef.current,
              lngLatFn,
              showOrdinalsRef.current
            ),
            cluster: true,
            clusterRadius: 20,
          })

          map.addLayer({
            id: "locations",
            type: "symbol",
            source: "locations",
            layout: {
              "icon-image": ["get", "icon"],
              "icon-offset": [0, -24],
              "icon-allow-overlap": true,
              "text-field": [
                "case",
                ["has", "ordinal"],
                ["to-string", ["get", "ordinal"]],
                "",
              ],
              "text-size": 11,
              "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
              "text-offset": [0, -2.9],
              "text-anchor": "center",
              "text-justify": "center",
              "text-allow-overlap": true,
              "text-ignore-placement": true,
            },
            paint: {
              "icon-color": getIconColourMap(
                categoryFieldRef.current,
                categoriesRef.current
              ),
              "icon-halo-color": "white",
              "icon-halo-width": 3,
              "text-color": "white",
              "text-halo-color": defaultPinColour,
              "text-halo-width": 0.5,
            },
          })

          map.addLayer({
            id: "clusters",
            type: "circle",
            source: "locations",
            filter: ["has", "point_count"],
            paint: {
              "circle-color": circleColor,
              "circle-radius": ["step", ["get", "point_count"], 20, 5, 30],
            },
          })

          map.addLayer({
            id: "cluster-count",
            type: "symbol",
            source: "locations",
            filter: ["has", "point_count"],
            layout: {
              "text-field": "{point_count_abbreviated}",
              "text-size": 12,
            },
            paint: {
              "text-color": textColor,
            },
          })
        })
      })
      if (distanceCondition?.value) {
        buildDistanceCircle(map, distanceCondition)
      }
    }

    function createMap() {
      const map = new mapboxgl.Map({
        container: currentMapId.current,
        style: `mapbox://styles/mapbox/${style}`,
        zoom: zoom,
        center: mapCenter,
        interactive: !isStatic,
      })

      map.on("load", () => {
        map.resize()

        if (hasCentralLocation || mapBoundsNotSaved) {
          recenterMap(map)
        }

        addMapData(map)

        map.on("click", "locations", handleLocationClick)

        map.on("mouseover", "locations", () => {
          map.getCanvas().style.cursor = "pointer"
        })

        map.on("mouseout", "locations", () => {
          map.getCanvas().style.cursor = ""
        })

        map.on("click", "clusters", (e) => {
          const features = map.queryRenderedFeatures(e.point, {
            layers: ["clusters"],
          })
          const clusterId = features[0].properties.cluster_id
          map
            .getSource("locations")
            .getClusterExpansionZoom(clusterId, (error, zoom) => {
              if (error) throw error

              map.easeTo({
                center: features[0].geometry.coordinates,
                zoom: zoom,
              })
            })
        })

        map.on("mouseover", "clusters", () => {
          map.getCanvas().style.cursor = "pointer"
        })
        map.on("mouseout", "clusters", () => {
          map.getCanvas().style.cursor = ""
        })

        map.on("style.load", () => {
          // Triggered when `setStyle` is called.
          addMapData(map)
        })
      })

      map.on("moveend", () => {
        const centerLngLat = map.getCenter()
        const newCenter = [centerLngLat.lng, centerLngLat.lat]
        zoomChangedDebounce(map.getZoom())
        saveMapBounds(newCenter)
      })

      // hide mapbox logo
      const mapElement = document.getElementById(currentMapId.current)
      mapElement.querySelector(".mapboxgl-ctrl-logo").style.display = "none"
      mapElement.querySelector(".mapboxgl-ctrl-attrib").style.display = "none"

      setMap(map)
    }

    function updateMap(locations, distanceCondition) {
      const mapSource = map.getSource("locations")
      if (!mapSource) return

      mapSource.setData(getMapData(locations, lngLatFn, showOrdinals))
      map.setPaintProperty(
        "locations",
        "icon-color",
        getIconColourMap(categoryField, categories)
      )
      const distanceSource = map.getSource("distance")
      if (distanceCondition?.value) {
        if (!distanceSource) {
          buildDistanceCircle(map, distanceCondition)
        } else {
          updateDistanceCircle(map, distanceCondition)
        }
      } else {
        if (distanceSource) {
          removeDistanceCircle(map)
        }
      }
    }

    if (hasLocations) {
      if (map) {
        updateMap(locations, distanceCondition)
      } else {
        createMap()
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locations,
    centralLocation,
    categoryField,
    categories,
    distanceCondition,
    showOrdinals,
  ])

  async function toggleMapStyle() {
    const newStyle =
      style === MAP_STYLES.streets ? MAP_STYLES.satellite : MAP_STYLES.streets

    map.setStyle(`mapbox://styles/mapbox/${newStyle}`)
    handleStyleChange(newStyle)
  }

  return (
    <Wrapper isStatic={isStatic}>
      {!hasLocations && isLoading && (
        <div className="font-italic shadow-black/2 mx-5 flex items-center justify-center rounded-md bg-white p-5 text-sm text-neutral-6 shadow">
          <InlineLoading />
        </div>
      )}
      {!hasLocations && !isLoading && (
        <div className="font-italic shadow-black/2 mx-5 flex items-center justify-center rounded-md bg-white p-5 text-sm text-neutral-6 shadow">
          There are no locations to map.
        </div>
      )}
      <div
        ref={mapContainerRef}
        id="map-container"
        className={classNames("map-container group rounded", {
          visible: hasLocations,
          invisible: !hasLocations,
        })}
      >
        <div
          className={classNames({
            "h-full w-full rounded": isStatic,
            map: !isStatic,
          })}
          id={currentMapId.current}
        >
          <div
            className={classNames("absolute left-4 z-10 flex space-x-2", {
              "top-4": buttonsLocation === "top",
              "bottom-4": buttonsLocation === "bottom",
              "invisible group-hover:visible": showButtonsOnHover,
            })}
          >
            {canEdit && (
              <>
                <MapButton
                  name={style === MAP_STYLES.streets ? "map" : "globe-americas"}
                  onClick={toggleMapStyle}
                />
                <MapButton
                  name="magnifying-glass-plus"
                  onClick={() => map.zoomIn()}
                  disabled={zoom >= MAP_MAX_ZOOM_LEVEL}
                />
                <MapButton
                  name="magnifying-glass-minus"
                  onClick={() => map.zoomOut()}
                  disabled={zoom <= MAP_MIN_ZOOM_LEVEL}
                />
                {canRecenterMap && (
                  <MapButton name="map-pin" onClick={() => recenterMap(map)} />
                )}
                {buttons.map((button, i) => (
                  <MapButton
                    key={i}
                    onClick={button.onClick}
                    name={button.iconName}
                  />
                ))}
              </>
            )}
          </div>
          {children}
        </div>
      </div>
    </Wrapper>
  )
}

// Hack so we could ship the side nav
function Wrapper({ isStatic, children }) {
  if (isStatic) {
    return <>{children}</>
  }

  return <div className="relative w-full">{children}</div>
}
